/**
 *   🤕🐒🐲 Attributes (Room Icons)
 *   Author: David Nuñez
 *   Date: 04 Oct 2018
 */

import React, { Fragment } from 'react';

/*
   The keys are ids:
   1, "Rolls Royce Transfer"
   2, "Private Car Transfer"
   3, "Room Service"
   4, "Concierge Service"
   5, "Butler Elite Service"
   6, "Handicap Friendly"
   7, "Love Nest / Togethernest"
   8, "Choice of Premium Liquors"
   9, "Premium Liquors"
   10, "Beer & Wine"
   11, "Free Airport Transfer"
   12, "Free Wi-Fi"
   13, "Tranquility Soaking Tubs"
   14, "In-Room Washer & Dryer"
 */

const { url, isBeaches = false } = window._app.brandOptions;

const attributes = {
  1: {
    className: 'icon-rolls-royce-transfer',
    attributeName: 'Rolls Royce Transfer',
    description: (
      <Fragment>
        This category also includes the exclusive indulgence of private
        transfers to and from the airport in chauffeur-driven Rolls Royce or
        Mercedes Benz.
      </Fragment>
    ),
  },
  2: {
    className: 'icon-private-car-transfer',
    attributeName: 'Private Car Transfer',
    description: (
      <Fragment>
        This category also includes the exclusive indulgence of private
        transfers to and from the airport in chauffeur-driven luxury private
        car.
      </Fragment>
    ),
  },
  3: {
    className: 'icon-room-service',
    attributeName: 'Room Service',
    description: (
      <Fragment>
        Whether it is a simple continental breakfast delivered to your room or a
        more elaborate dinner affair, room service is another ultra all
        inclusive exclusive.
      </Fragment>
    ),
  },
  4: {
    className: 'icon-concierge-service',
    attributeName: 'Concierge Service',
    description: (
      <Fragment>
        This room category includes access to the exclusive amenities &amp;
        extra pampering of{' '}
        <a target="_blank" href={`${url}/all-inclusive/sandals-club/`}>
          Club Sandals
        </a>
        , featuring such additional perks as a private check-in, dedicated
        lounge, stocked in-room bars &amp; much more!
      </Fragment>
    ),
  },
  5: {
    className: 'icon-butler-service',
    attributeName: 'Butler Service',
    description: (
      <Fragment>
        This suite features the intuitive services of your very own{' '}
        <a target="_blank" href={`${url}/all-inclusive/butler-service/`}>
          personal butler
        </a>{' '}
        who will pamper you in extraordinary ways throughout your stay.
      </Fragment>
    ),
  },
  6: {
    className: 'icon-handicap-friendly',
    attributeName: 'Handicap Friendly',
    description: (
      <Fragment>
        This room is wheelchair accessible and features special amenities. For
        more information, please review our{' '}
        <a target="_blank" href={`${url}/accessibility-guide/`}>
          Accessibility Guide
        </a>
      </Fragment>
    ),
  },
  7: {
    className: 'icon-love-nest',
    attributeName: 'Love Nest',
    description: (
      <Fragment>
        Defined by exclusive amenities and unique designs for heavenly
        seclusion, these{' '}
        <a target="_blank" href={`${url}/accommodations/love-nest/`}>
          romantic luxury suites and villas
        </a>{' '}
        create the ultimate escape for love birds.
      </Fragment>
    ),
  },
  8: {
    className: 'icon-choice-premium-spirits',
    attributeName: 'In-Room Bar w/ Premium Liquors',
    description: (
      <Fragment>
        This room boasts a fully stocked bar with juice, soda, water, sparkling
        wine, Robert Mondavi Twin Oaks<sup>&reg;</sup> wine, local beer and your
        selection of premium liquors, replenished upon request.
      </Fragment>
    ),
  },
  9: {
    className: 'icon-premium-spirits',
    attributeName: 'In-Room Bar w/ Select Liquors',
    description: (
      <Fragment>
        This room features a well-stocked bar with juice, soda, water, Robert
        Mondavi Twin Oaks<sup>&reg;</sup> wine, local beer and a variety of
        spirits replenished daily.
      </Fragment>
    ),
  },
  10: {
    className: 'icon-beer-wine',
    attributeName: 'In-Room Local Beer & Wine',
    description: (
      <Fragment>
        This room includes juice, soda, water, Robert Mondavi Twin Oaks
        <sup>&reg;</sup> wine and local beer replenished daily.
      </Fragment>
    ),
  },
  11: {
    className: 'icon-airport-transfers',
    attributeName: 'Airport Transfers',
    description: (
      <Fragment>
        Relax and enjoy your stress-free ride with non-stop resort transfers to
        and from the airport, featuring air-conditioning and comfortable,
        spacious seating.
      </Fragment>
    ),
  },
  12: {
    className: 'icon-free-wifi',
    attributeName: 'Free Wifi',
    description: <Fragment>Free Wi-Fi is included.</Fragment>,
  },
  13: {
    className: 'icon-soaking-tub',
    attributeName: 'Soaking Tubs',
    description: (
      <Fragment>A whole new way to celebrate romance al fresco.</Fragment>
    ),
    learnMore: '/all-inclusive/soaking-tubs/',
  },
  14: {
    className: 'icon-washer-dryer',
    attributeName: 'In-Room Washer & Dryer',
    description: (
      <Fragment>
        This room category includes a washer & dryer inside the room. Take care
        of all your laundry needs without leaving the comfort of your suite with
        your in-room washer & dryer.
      </Fragment>
    ),
  },
  15: {
    className: 'icon-bmw-transfer',
    attributeName: 'Private BMW Transfer',
    description: (
      <Fragment>
        This category also includes the exclusive indulgence of private
        transfers to and from the airport in a chauffeur-driven BMW luxury
        vehicle.
      </Fragment>
    ),
  },
  16: {
    className: 'icon-vip-transfer',
    attributeName: 'VIP Arrival',
    description: (
      <Fragment>
        This category also includes VIP arrival service including expedited
        immigration, personal airport greeting, private luxury transfer
        coordination, and access to arrival lounge amenities.
      </Fragment>
    ),
  },
  17: {
    className: 'icon-mini-transfer',
    attributeName: 'Convertible MINI Cooper',
    description: (
      <Fragment>
        Complimentary use of a stylish and sporty MINI Cooper to explore the
        island.
      </Fragment>
    ),
  },
  18: {
    className: 'icon-private-pool',
    attributeName: 'Private Pool',
    description: (
      <Fragment>
        This suite features direct access to a private pool sanctuary just steps from your patio, creating a seamless indoor-outdoor experience.
      </Fragment>
    )
  },
  19: {
    className: 'icon-sky-pool',
    attributeName: 'Skypool',
    description: (
      <Fragment>
        This suite features a balcony with a private cantilevered infinity-edge plunge pool where you can admire the stunning view.
      </Fragment>
    )
  },
  20: {
    className: 'icon-swim-up-pool',
    attributeName: 'Swim-up Pool',
    description: (
      <Fragment>
        This suite features the indulgence of a semi-private swim-up pool, accessible directly from your patio.
      </Fragment>
    )
  },
  21: {
    className: 'icon-in-suite-fitness-room',
    attributeName: 'In-Suite Fitness Room',
    description: (
      <Fragment>
        This suite includes a separate room furnished with top-of-the-line fitness equipment.
      </Fragment>
    )
  },
  22: {
    className: 'icon-in-suite-media-room',
    attributeName: 'In-Suite Media Room',
    description: (
      <Fragment>
        This suite includes a separate room outfitted with a large flat-screen TV and cozy seating area.
      </Fragment>
    )
  },
  23: {
    className: 'icon-boat-transfer',
    attributeName: 'Boat Transfers',
    description: (
      <Fragment>
        This category includes the option to choose complimentary boat transfers to and from the airport, featuring an onboard bar with local beer, water, and packaged snacks.
      </Fragment>
    )
  }
};

if (isBeaches) {
  attributes[4] = {
    className: 'icon-concierge-service',
    attributeName: 'Concierge Service',
    description: (
      <Fragment>
        This room category includes access to the exclusive amenities &amp;
        extra pampering of{' '}
        {
          <a
            href="http://www.beaches.com/all-inclusive/concierge-service/"
            target="_blank"
          >
            Suite Concierge service
          </a>
        }
        , featuring such additional perks as a private check-in, dedicated
        lounge, stocked in-room bars &amp; much more!
      </Fragment>
    ),
  };

  attributes[6] = {
    className: 'icon-handicap-friendly',
    attributeName: 'Handicap Friendly',
    description: (
      <Fragment>
        This room is wheelchair accessible and features special amenities. For
        room-specific accessibility amenities, please view our{' '}
        {
          <a target="_blank" href={`${url}/accessibility-guide/`}>
            Accessibility Guide
          </a>
        }
      </Fragment>
    ),
  };

  attributes[7] = {
    className: 'icon-together-nest',
    attributeName: 'Together Nest Suites',
    description: (
      <Fragment>
        Defined by exclusive amenities and unique designs for heavenly
        seclusion, these{' '}
        {
          <a href="http://www.beaches.com/together-nest/" target="_blank">
            romantic luxury suites and villas
          </a>
        }{' '}
        create the ultimate escape for love birds.
      </Fragment>
    ),
  };

  attributes[8] = {
    className: 'icon-choice-premium-spirits',
    attributeName: 'In-Room Bar w/ Premium Liquors',
    description: (
      <Fragment>
        This suite features a fully stocked bar with juices, sodas, water, beer,
        and your selection of premium spirits and Robert Mondavi Twin Oaks
        <sup>&reg;</sup> wine that is restocked daily. Alcoholic beverages are
        available upon request.
      </Fragment>
    ),
  };

  attributes[9] = {
    className: 'icon-premium-spirits',
    attributeName: 'In-Room Bar w/ Select Liquors',
    description: (
      <Fragment>
        This suite features a fully stocked bar with juices, sodas, water, beer,
        premium spirits, and Robert Mondavi Twin Oaks<sup>&reg;</sup> wine that
        is restocked daily. Alcoholic beverages are available upon request.
      </Fragment>
    ),
  };
}

export default attributes;



// WEBPACK FOOTER //
// ./js/modules/attributes.js